import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { Color } from "color-core";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Simple wrapper around culori for our common color conversion needs
export const convertColor = (
  from: string,
  to: "rgb" | "rgba" | "hsl" | "hex" | "oklch",
  options: { opacity?: number; flat?: boolean } = { opacity: 1, flat: false }
) => {
  if (!from) return from;

  let color: Color;

  // check if from color same is same type as "to" type, return as is
  if (from.includes(to)) {
    return from;
  }

  if (from.includes("oklch")) {
    // oklch(0.208 0.042 265.755)
    const oklchMatch = from.match(
      /oklch\((\d+(\.\d+)?)\s+(\d+(\.\d+)?)\s+(\d+(\.\d+)?)\)/
    );

    color = new Color({
      L: parseFloat(oklchMatch[1]),
      C: parseFloat(oklchMatch[3]),
      h: parseFloat(oklchMatch[5]),
    });
  } else {
    color = new Color(from);
  }

  if (options?.opacity) {
    color.adjustAlpha(options.opacity);
  }

  switch (to) {
    case "rgb": {
      const { r, g, b } = color.toRgb();
      return options.flat ? `${r} ${g} ${b}` : `rgb(${r}, ${g}, ${b})`;
    }
    case "rgba": {
      const { r, g, b, a } = color.toRgb();
      return options.flat
        ? `${r} ${g} ${b} ${a}`
        : `rgba(${r}, ${g}, ${b}, ${a})`;
    }
    case "hsl": {
      const { h, s, l } = color.toHsl();
      return options.flat
        ? `${h} ${s * 100}% ${l * 100}%`
        : `hsl(${h}, ${s * 100}%, ${l * 100}%)`;
    }
    case "hex": {
      return color.toHex();
    }
    case "oklch": {
      const { L, C, h } = color.toOklch();

      // Ensure proper formatting with correct units
      // L should be 0-1 (with % for CSS)
      // C should typically be 0-0.4 (with proper unit)
      // h should be 0-360 (with deg for CSS)
      return options.flat
        ? `${L.toFixed(2)} ${C.toFixed(4)} ${h.toFixed(2)}`
        : `oklch(${L.toFixed(2)} ${C.toFixed(4)} ${h.toFixed(2)}deg)`;
    }
    default:
      throw new Error(`Unsupported color conversion: ${to}`);
  }
};

// Helper function for flattening HSL string
const flattenHslString = (hsl: string): string => {
  // Remove "hsl(" and ")" and replace commas with spaces
  return hsl.replace(/hsl\(|\)/g, "").replace(/,\s*/g, " ");
};

// Keep any utility functions that might still be useful
export const isHexColor = (color: string) =>
  /^#([0-9A-F]{3,4}|[0-9A-F]{6}|[0-9A-F]{8})$/i.test(color);

export const isFlatHsl = (hsl: string) => {
  return (
    hsl.match(/\d{1,3}\s\d{1,3}%\s\d{1,3}%/) &&
    !hsl.includes("(") &&
    !hsl.includes(")")
  );
};
